import { el, qs } from '../utils/dom.js';

/**
 * Tab bar for multiple open maps.
 * Each tab holds a snapshot of editor state that gets swapped on tab switch.
 */
export class TabBar {
  constructor(state, mapCanvas, compositor) {
    this.state = state;
    this.mapCanvas = mapCanvas;
    this.compositor = compositor;
    this.container = qs('#tab-bar');
    this._tabs = []; // { id, name, stateSnapshot }
    this._activeTabId = null;
    this._nextId = 1;

    // Show tab bar
    this.container.style.height = '28px';
    this.container.style.display = 'flex';
    this.container.style.alignItems = 'center';
    this.container.style.borderBottom = '1px solid var(--border)';
    this.container.style.padding = '0 4px';
    this.container.style.gap = '2px';
    this.container.style.overflow = 'hidden';

    // Create initial tab
    this.addTab(state.get('fileName') || 'Untitled');

    state.onChange('fileName', (name) => {
      const tab = this._tabs.find(t => t.id === this._activeTabId);
      if (tab) {
        tab.name = name || 'Untitled';
        this._render();
      }
    });
  }

  addTab(name) {
    const id = this._nextId++;
    this._tabs.push({ id, name: name || 'Untitled', stateSnapshot: null });

    // Save current tab's state before switching
    if (this._activeTabId !== null) {
      this._saveActiveState();
    }

    this._activeTabId = id;
    // Save initial snapshot immediately so tab is always restorable
    this._saveActiveState();
    this._render();
    return id;
  }

  switchTab(id) {
    if (id === this._activeTabId) return;

    // Save current state
    this._saveActiveState();

    // Restore target state
    const tab = this._tabs.find(t => t.id === id);
    if (!tab || !tab.stateSnapshot) return;

    this._activeTabId = id;
    this._restoreState(tab.stateSnapshot);
    this.mapCanvas.clear();
    this.mapCanvas.invalidateAll();
    this.compositor.requestRender();
    this._render();
  }

  closeTab(id) {
    const idx = this._tabs.findIndex(t => t.id === id);
    if (idx === -1 || this._tabs.length <= 1) return;

    this._tabs.splice(idx, 1);

    if (this._activeTabId === id) {
      const newIdx = Math.min(idx, this._tabs.length - 1);
      this._activeTabId = this._tabs[newIdx].id;
      if (this._tabs[newIdx].stateSnapshot) {
        this._restoreState(this._tabs[newIdx].stateSnapshot);
        this.mapCanvas.clear();
        this.mapCanvas.invalidateAll();
        this.compositor.requestRender();
      }
    }

    this._render();
  }

  _saveActiveState() {
    const tab = this._tabs.find(t => t.id === this._activeTabId);
    if (!tab) return;

    tab.stateSnapshot = {
      mapWidth: this.state.get('mapWidth'),
      mapHeight: this.state.get('mapHeight'),
      tileWidth: this.state.get('tileWidth'),
      tileHeight: this.state.get('tileHeight'),
      layers: this.state.get('layers').map(l => ({
        ...l,
        data: l.data ? new Uint32Array(l.data) : undefined,
        objects: l.objects ? [...l.objects] : undefined,
      })),
      tilesets: [...this.state.get('tilesets')],
      tilesetImages: new Map(this.state.get('tilesetImages')),
      properties: { ...this.state.get('properties') },
      fileName: this.state.get('fileName'),
      scrollX: this.state.get('scrollX'),
      scrollY: this.state.get('scrollY'),
      zoom: this.state.get('zoom'),
      activeLayerIndex: this.state.get('activeLayerIndex'),
      dirty: this.state.get('dirty'),
    };
    tab.name = this.state.get('fileName') || 'Untitled';
  }

  _restoreState(snapshot) {
    this.state.batch(snapshot);
  }

  _render() {
    this.container.innerHTML = '';

    for (const tab of this._tabs) {
      const isActive = tab.id === this._activeTabId;
      const tabEl = el('div', {
        className: `tab${isActive ? ' tab-active' : ''}`,
        onClick: () => this.switchTab(tab.id),
      });

      tabEl.appendChild(el('span', { className: 'tab-name' }, tab.name));

      if (this._tabs.length > 1) {
        const closeBtn = el('span', {
          className: 'tab-close',
          onClick: (e) => { e.stopPropagation(); this.closeTab(tab.id); },
        }, '\u00D7');
        tabEl.appendChild(closeBtn);
      }

      this.container.appendChild(tabEl);
    }

    // New tab button
    this.container.appendChild(el('div', {
      className: 'tab tab-new',
      onClick: () => {
        this.addTab('Untitled');
        this.state.createBlankMap(40, 30);
        this.mapCanvas.clear();
        this.mapCanvas.invalidateAll();
        this.compositor.requestRender();
      },
    }, '+'));
  }
}
